// src/pages/orderAssignment/ManualOrderAssignmentPage.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users as UsersIcon,
  ArrowLeft,
  Play,
  Package,
  DollarSign,
  AlertCircle,
  Info,
  X,
  Plus,
  Tag,
} from 'lucide-react';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import {
  assignOrders,
  getShopAssignmentStats,
  reassignOrders,
} from '../../services/api/orderAssignments';
import { getShops } from '../../services/api/shops';
import { getUsers } from '../../services/api/users';
import { getProducts } from '../../services/api/products';
import { getEcomOrders } from '../../services/api/orders';
import { formatCurrency } from '../../utils/orderCalculations';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import MultiSelectFilter from '../../components/common/MultiSelectFilter/MultiSelectFilter';
import AssignmentOverviewTable from './components/AssignmentOverviewTable';
import ReassignModal from './components/ReassignModal';
import styles from './ManualOrderAssignmentPage.module.css';

const ManualOrderAssignmentPage = () => {
  const navigate = useNavigate();
  const { t } = useLocalization();
  const { success, error: showError } = useAlert();

  // ── Data ────────────────────────────────────────────────────────────
  const [isLoading, setIsLoading] = useState(true);
  const [isAssigning, setIsAssigning] = useState(false);
  const [isReassigning, setIsReassigning] = useState(false);

  const [shops, setShops] = useState([]);
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);

  const [selectedShopId, setSelectedShopId] = useState(null);

  // ── Overview stats ───────────────────────────────────────────────────
  const [shopStats, setShopStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  // ── Reassign modal ───────────────────────────────────────────────────
  const [reassignModalOpen, setReassignModalOpen] = useState(false);
  const [reassignSourceUsers, setReassignSourceUsers] = useState([]);
  // 'reassign' | 'unassigned'
  const [reassignModalMode, setReassignModalMode] = useState('reassign');

  // ── Assignment criteria ──────────────────────────────────────────────
  const [assignmentCriteria, setAssignmentCriteria] = useState({
    grandTotalRules: [],
    productRules: [],
    defaultUserIds: [],
  });

  // ── Status filter for new assignment ────────────────────────────────
  const [filterByStatus, setFilterByStatus] = useState(false);
  const [selectedStatusKeys, setSelectedStatusKeys] = useState([]);

  const [errors, setErrors] = useState({});

  // ── Load initial data ────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true);
        const [shopsRes, usersRes, productsRes] = await Promise.all([
          getShops({ assigned: true, type: 'online,dropshipper' }),
          getUsers({ limit: 1000 }),
          getProducts({ limit: 1000 }),
        ]);

        setShops(shopsRes || []);
        setUsers(usersRes?.users || []);
        setProducts(productsRes?.products || []);

        const savedShopId = sessionStorage.getItem('manualAssignmentShopId');
        const defaultShop =
          shopsRes?.find((s) => (savedShopId ? s.id === parseInt(savedShopId) : s.isDefault)) ||
          shopsRes?.[0];

        if (defaultShop) setSelectedShopId(defaultShop.id);
      } catch {
        showError(t('orderAssignment.loadDataFailed'));
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [showError, t]);

  // ── Load orders when shop changes ────────────────────────────────────
  useEffect(() => {
    if (!selectedShopId) return;
    const loadOrders = async () => {
      try {
        const res = await getEcomOrders({
          shopId: selectedShopId,
          isInPacking: false,
          isDispatched: false,
          limit: 2000,
        });
        setOrders(res.orders || []);
      } catch {
        showError(t('orderAssignment.loadOrdersFailed'));
      }
    };
    loadOrders();
  }, [selectedShopId, showError, t]);

  // ── Load overview stats ──────────────────────────────────────────────
  const loadStats = useCallback(async () => {
    if (!selectedShopId) return;
    setStatsLoading(true);
    try {
      const data = await getShopAssignmentStats(selectedShopId);
      setShopStats(data);
    } catch {
      showError('Failed to load assignment overview');
    } finally {
      setStatsLoading(false);
    }
  }, [selectedShopId, showError]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  // ── All statuses from shop stats (for status filter chips) ───────────
  const availableStatuses = useMemo(() => shopStats?.statuses || [], [shopStats]);

  // ── Shop change ──────────────────────────────────────────────────────
  const handleShopChange = (shopId) => {
    setSelectedShopId(shopId);
    sessionStorage.setItem('manualAssignmentShopId', shopId.toString());
    setAssignmentCriteria({ grandTotalRules: [], productRules: [], defaultUserIds: [] });
    setFilterByStatus(false);
    setSelectedStatusKeys([]);
    setShopStats(null);
  };

  // ── Grand total rule handlers ────────────────────────────────────────
  const addGrandTotalRule = () =>
    setAssignmentCriteria((prev) => ({
      ...prev,
      grandTotalRules: [...prev.grandTotalRules, { min: 0, max: null, userIds: [] }],
    }));

  const updateGrandTotalRule = (index, field, value) =>
    setAssignmentCriteria((prev) => ({
      ...prev,
      grandTotalRules: prev.grandTotalRules.map((r, i) => (i === index ? { ...r, [field]: value } : r)),
    }));

  const removeGrandTotalRule = (index) =>
    setAssignmentCriteria((prev) => ({
      ...prev,
      grandTotalRules: prev.grandTotalRules.filter((_, i) => i !== index),
    }));

  // ── Product rule handlers ────────────────────────────────────────────
  const addProductRule = () =>
    setAssignmentCriteria((prev) => ({
      ...prev,
      productRules: [...prev.productRules, { productIds: [], userIds: [] }],
    }));

  const updateProductRule = (index, field, value) =>
    setAssignmentCriteria((prev) => ({
      ...prev,
      productRules: prev.productRules.map((r, i) => (i === index ? { ...r, [field]: value } : r)),
    }));

  const removeProductRule = (index) =>
    setAssignmentCriteria((prev) => ({
      ...prev,
      productRules: prev.productRules.filter((_, i) => i !== index),
    }));

  // ── Validation ───────────────────────────────────────────────────────
  const validateAssignment = () => {
    const errs = {};

    if (!selectedShopId) errs.shop = t('orderAssignment.shopRequired');

    if (filterByStatus && selectedStatusKeys.length === 0) {
      errs.statusFilter = 'Please select at least one status to filter by';
    }

    assignmentCriteria.grandTotalRules.forEach((rule, i) => {
      if (rule.userIds.length === 0) errs[`gt_${i}_users`] = t('orderAssignment.atLeastOneUser');
      if (rule.min !== null && rule.max !== null && rule.min >= rule.max)
        errs[`gt_${i}_range`] = t('orderAssignment.maxMustBeGreaterThanMin');
    });

    assignmentCriteria.productRules.forEach((rule, i) => {
      if (rule.productIds.length === 0) errs[`prod_${i}_products`] = t('orderAssignment.atLeastOneProduct');
      if (rule.userIds.length === 0) errs[`prod_${i}_users`] = t('orderAssignment.atLeastOneUser');
    });

    const hasRules =
      assignmentCriteria.grandTotalRules.length > 0 || assignmentCriteria.productRules.length > 0;
    if (!hasRules && assignmentCriteria.defaultUserIds.length === 0) {
      errs.general = t('orderAssignment.atLeastOneRuleOrDefault');
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ── Execute assignment ───────────────────────────────────────────────
  const handleExecuteAssignment = async () => {
    if (!validateAssignment()) {
      showError(t('orderAssignment.fixValidationBeforeExecuting'));
      return;
    }

    const payload = {
      shopId: selectedShopId,
      assignmentCriteria,
      statusKeys: filterByStatus && selectedStatusKeys.length > 0 ? selectedStatusKeys : undefined,
    };

    if (!window.confirm(t('orderAssignment.confirmAssignment', { count: '?' }))) return;

    setIsAssigning(true);
    try {
      const response = await assignOrders(payload);
      success(t('orderAssignment.successfullyAssigned', { count: response.assignedOrders }), {
        title: t('orderAssignment.assignmentComplete'),
        duration: 4000,
      });
      setAssignmentCriteria({ grandTotalRules: [], productRules: [], defaultUserIds: [] });
      setFilterByStatus(false);
      setSelectedStatusKeys([]);
      const ordersRes = await getEcomOrders({
        shopId: selectedShopId,
        isInPacking: false,
        isDispatched: false,
        limit: 2000,
      });
      setOrders(ordersRes.orders || []);
      await loadStats();
    } catch (err) {
      showError(err.response?.data?.message || t('orderAssignment.assignmentFailed'));
    } finally {
      setIsAssigning(false);
    }
  };

  // ── Reassign flow ────────────────────────────────────────────────────
  const handleOpenReassign = (selectedUsers) => {
    setReassignSourceUsers(selectedUsers);
    setReassignModalMode('reassign');
    setReassignModalOpen(true);
  };

  // ── Assign unassigned flow ───────────────────────────────────────────
  const handleOpenAssignUnassigned = () => {
    setReassignSourceUsers([]);
    setReassignModalMode('unassigned');
    setReassignModalOpen(true);
  };

  const handleReassignModalClose = () => {
    setReassignModalOpen(false);
    setReassignSourceUsers([]);
    setReassignModalMode('reassign');
  };

  /**
   * Unified confirm handler for both 'reassign' and 'unassigned' modes.
   * In 'unassigned' mode we call the /assign endpoint with unassignedOnly=true.
   * In 'reassign' mode we call /reassign as before.
   */
  const handleReassignConfirm = async (statusKeys, criteria) => {
    setIsReassigning(true);
    try {
      if (reassignModalMode === 'unassigned') {
        // Use the assign endpoint with unassignedOnly flag
        const response = await assignOrders({
          shopId: selectedShopId,
          assignmentCriteria: criteria,
          statusKeys: statusKeys || undefined,
          unassignedOnly: true,
        });
        success(
          t('orderAssignment.successfullyAssigned', { count: response.assignedOrders }),
          { title: t('orderAssignment.assignmentComplete'), duration: 4000 },
        );
      } else {
        const response = await reassignOrders({
          shopId: selectedShopId,
          sourceUserIds: reassignSourceUsers.map((u) => u.userId),
          statusKeys,
          assignmentCriteria: criteria,
        });
        success(
          t('orderAssignment.reassignSuccess', { count: response.assignedOrders }),
          { title: t('orderAssignment.assignmentComplete'), duration: 4000 },
        );
      }

      setReassignModalOpen(false);
      setReassignSourceUsers([]);
      setReassignModalMode('reassign');
      await loadStats();
    } catch (err) {
      showError(
        err.response?.data?.message ||
          (reassignModalMode === 'unassigned'
            ? t('orderAssignment.assignmentFailed')
            : t('orderAssignment.reassignFailed')),
      );
    } finally {
      setIsReassigning(false);
    }
  };

  // ── Format options ────────────────────────────────────────────────────
  const userOptions = users.map((u) => ({
    key: u.id,
    label: `${u.name}${u.role?.name ? ` (${u.role.name})` : ''}`,
  }));

  const productOptions = products.map((p) => ({
    key: p.id,
    label: `${p.name}${p.sku ? ` (${p.sku})` : ''}`,
  }));

  // ── Loading skeleton ─────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className={styles.assignmentPage}>
        <PageHeader
          title={t('orderAssignment.manualAssignmentTitle')}
          description={t('orderAssignment.manualAssignmentSubtitle')}
          icon={UsersIcon}
        />
        <div className={styles.pageContent}>
          <div className={styles.loadingState}>
            <div className={styles.loadingSpinnerLarge} />
            <h3>{t('orderAssignment.loadingAssignmentData')}</h3>
            <p>{t('orderAssignment.loadingAssignmentMessage')}</p>
          </div>
        </div>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <div className={styles.assignmentPage}>
      <PageHeader
        title={t('orderAssignment.manualAssignmentTitle')}
        description={t('orderAssignment.manualAssignmentSubtitle')}
        icon={UsersIcon}
        actions={
          <button onClick={() => navigate('/orders')} className={styles.backButton}>
            <ArrowLeft size={20} />
            {t('orderAssignment.backToOrders')}
          </button>
        }
      />

      <div className={styles.pageContent}>
        <div className={styles.assignmentContainer}>

          {/* ── Shop Selection ────────────────────────────────────────── */}
          <div className={styles.configSection}>
            <div className={styles.sectionHeader}>
              <h2>{t('orderAssignment.shopSelection')}</h2>
            </div>
            <div className={styles.sectionContent}>
              <div className={styles.formGroup}>
                <label htmlFor="shopSelect">{t('orderAssignment.selectShop')} *</label>
                <select
                  id="shopSelect"
                  value={selectedShopId || ''}
                  onChange={(e) => handleShopChange(parseInt(e.target.value))}
                  className={styles.select}
                >
                  <option value="">{t('orderAssignment.selectShopPlaceholder')}</option>
                  {shops.map((shop) => (
                    <option key={shop.id} value={shop.id}>
                      {shop.name} ({shop.type})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {selectedShopId && (
            <>
              {/* ── Assignment Overview Table ─────────────────────────── */}
              <AssignmentOverviewTable
                stats={shopStats}
                isLoading={statsLoading}
                onReassign={handleOpenReassign}
                onAssignUnassigned={handleOpenAssignUnassigned}
                onRefresh={loadStats}
              />

              {/* General error */}
              {errors.general && (
                <div className={styles.errorBanner}>
                  <AlertCircle size={20} />
                  <p>{errors.general}</p>
                </div>
              )}

              {/* ── New Assignment Configuration ──────────────────────── */}
              <div className={styles.configSection}>
                <div className={styles.sectionHeader}>
                  <h2>{t('orderAssignment.assignmentConfiguration')}</h2>
                </div>

                <div className={styles.sectionContent}>

                  {/* Status filter */}
                  <div className={styles.ruleSection}>
                    <div className={styles.ruleSectionHeader}>
                      <div className={styles.sectionTitleRow}>
                        <Tag className={styles.sectionIcon} size={20} />
                        <h3>{t('orderAssignment.statusFilter')}</h3>
                      </div>
                    </div>
                    <div className={styles.infoBox}>
                      <Info size={16} />
                      <p>{t('orderAssignment.statusFilterInfo')}</p>
                    </div>

                    <label className={styles.toggleLabel}>
                      <input
                        type="checkbox"
                        checked={filterByStatus}
                        onChange={(e) => {
                          setFilterByStatus(e.target.checked);
                          if (!e.target.checked) setSelectedStatusKeys([]);
                        }}
                      />
                      <span>{t('orderAssignment.filterByStatus')}</span>
                    </label>

                    {filterByStatus && (
                      <div className={styles.statusChipsWrap}>
                        {availableStatuses.length > 0 ? (
                          <div className={styles.statusChips}>
                            {availableStatuses.map((s) => {
                              const active = selectedStatusKeys.includes(s.key);
                              return (
                                <button
                                  key={s.key}
                                  className={`${styles.statusChip} ${active ? styles.statusChipActive : ''}`}
                                  style={active ? { borderColor: s.colorCode, color: s.colorCode } : {}}
                                  onClick={() =>
                                    setSelectedStatusKeys((prev) =>
                                      active ? prev.filter((k) => k !== s.key) : [...prev, s.key]
                                    )
                                  }
                                  type="button"
                                >
                                  <span
                                    className={styles.statusDot}
                                    style={{ backgroundColor: s.colorCode }}
                                  />
                                  {s.label}
                                </button>
                              );
                            })}
                          </div>
                        ) : (
                          <p className={styles.noStatusesHint}>
                            No order statuses found. Select a shop with active orders.
                          </p>
                        )}
                        {errors.statusFilter && (
                          <span className={styles.errorText}>{errors.statusFilter}</span>
                        )}
                        {selectedStatusKeys.length > 0 && (
                          <p className={styles.statusFilterActive}>
                            {t('orderAssignment.statusFilterActive', { count: selectedStatusKeys.length })}
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Grand Total Rules */}
                  <div className={styles.ruleSection}>
                    <div className={styles.ruleSectionHeader}>
                      <div className={styles.sectionTitleRow}>
                        <DollarSign className={styles.sectionIcon} size={20} />
                        <h3>{t('orderAssignment.grandTotalRules')}</h3>
                      </div>
                      <button onClick={addGrandTotalRule} className={styles.addButton}>
                        <Plus size={16} />
                        {t('orderAssignment.addRule')}
                      </button>
                    </div>

                    {assignmentCriteria.grandTotalRules.length > 0 ? (
                      <div className={styles.rulesList}>
                        {assignmentCriteria.grandTotalRules.map((rule, index) => (
                          <div key={index} className={styles.ruleCard}>
                            <div className={styles.ruleHeader}>
                              <h4>{t('orderAssignment.ruleNumber', { number: index + 1 })}</h4>
                              <button
                                onClick={() => removeGrandTotalRule(index)}
                                className={styles.deleteButton}
                              >
                                <X size={16} />
                              </button>
                            </div>
                            <div className={styles.ruleFields}>
                              <div className={styles.rangeInputs}>
                                <div className={styles.formGroup}>
                                  <label>{t('orderAssignment.minimumAmount')}</label>
                                  <input
                                    type="number"
                                    value={rule.min === null ? '' : rule.min}
                                    onChange={(e) =>
                                      updateGrandTotalRule(
                                        index,
                                        'min',
                                        e.target.value === '' ? null : parseFloat(e.target.value),
                                      )
                                    }
                                    placeholder={t('orderAssignment.noMinimum')}
                                    step="0.01"
                                    className={styles.input}
                                  />
                                </div>
                                <div className={styles.rangeSeparator}>{t('orderAssignment.to')}</div>
                                <div className={styles.formGroup}>
                                  <label>{t('orderAssignment.maximumAmount')}</label>
                                  <input
                                    type="number"
                                    value={rule.max === null ? '' : rule.max}
                                    onChange={(e) =>
                                      updateGrandTotalRule(
                                        index,
                                        'max',
                                        e.target.value === '' ? null : parseFloat(e.target.value),
                                      )
                                    }
                                    placeholder={t('orderAssignment.noMaximum')}
                                    step="0.01"
                                    className={styles.input}
                                  />
                                </div>
                              </div>
                              {errors[`gt_${index}_range`] && (
                                <span className={styles.errorText}>{errors[`gt_${index}_range`]}</span>
                              )}
                              <div className={styles.formGroup}>
                                <label>{t('orderAssignment.assignTo')} *</label>
                                <MultiSelectFilter
                                  options={userOptions}
                                  selectedValues={rule.userIds}
                                  onChange={(ids) => updateGrandTotalRule(index, 'userIds', ids)}
                                  placeholder={t('orderAssignment.selectUsers')}
                                  searchPlaceholder={t('orderAssignment.searchUsers')}
                                  displayKey="label"
                                  valueKey="key"
                                  error={errors[`gt_${index}_users`]}
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className={styles.emptyState}>
                        <DollarSign size={48} />
                        <p>{t('orderAssignment.noGrandTotalRules')}</p>
                      </div>
                    )}
                  </div>

                  {/* Product Rules */}
                  <div className={styles.ruleSection}>
                    <div className={styles.ruleSectionHeader}>
                      <div className={styles.sectionTitleRow}>
                        <Package className={styles.sectionIcon} size={20} />
                        <h3>{t('orderAssignment.productRules')}</h3>
                      </div>
                      <button onClick={addProductRule} className={styles.addButton}>
                        <Plus size={16} />
                        {t('orderAssignment.addRule')}
                      </button>
                    </div>

                    {assignmentCriteria.productRules.length > 0 ? (
                      <div className={styles.rulesList}>
                        {assignmentCriteria.productRules.map((rule, index) => (
                          <div key={index} className={styles.ruleCard}>
                            <div className={styles.ruleHeader}>
                              <h4>{t('orderAssignment.ruleNumber', { number: index + 1 })}</h4>
                              <button
                                onClick={() => removeProductRule(index)}
                                className={styles.deleteButton}
                              >
                                <X size={16} />
                              </button>
                            </div>
                            <div className={styles.ruleFields}>
                              <div className={styles.formGroup}>
                                <label>{t('orderAssignment.products')} *</label>
                                <MultiSelectFilter
                                  options={productOptions}
                                  selectedValues={rule.productIds}
                                  onChange={(ids) => updateProductRule(index, 'productIds', ids)}
                                  placeholder={t('orderAssignment.selectProducts')}
                                  searchPlaceholder={t('orderAssignment.searchProducts')}
                                  displayKey="label"
                                  valueKey="key"
                                  error={errors[`prod_${index}_products`]}
                                />
                              </div>
                              <div className={styles.formGroup}>
                                <label>{t('orderAssignment.assignTo')} *</label>
                                <MultiSelectFilter
                                  options={userOptions}
                                  selectedValues={rule.userIds}
                                  onChange={(ids) => updateProductRule(index, 'userIds', ids)}
                                  placeholder={t('orderAssignment.selectUsers')}
                                  searchPlaceholder={t('orderAssignment.searchUsers')}
                                  displayKey="label"
                                  valueKey="key"
                                  error={errors[`prod_${index}_users`]}
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className={styles.emptyState}>
                        <Package size={48} />
                        <p>{t('orderAssignment.noProductRules')}</p>
                      </div>
                    )}
                  </div>

                  {/* Default Assignment */}
                  <div className={styles.defaultSection}>
                    <div className={styles.sectionTitleRow}>
                      <UsersIcon className={styles.sectionIcon} size={20} />
                      <h3>{t('orderAssignment.defaultAssignment')}</h3>
                    </div>
                    <div className={styles.infoBox}>
                      <Info size={16} />
                      <p>{t('orderAssignment.defaultAssignmentNote')}</p>
                    </div>
                    <div className={styles.formGroup}>
                      <label>{t('orderAssignment.defaultAssignees')}</label>
                      <MultiSelectFilter
                        options={userOptions}
                        selectedValues={assignmentCriteria.defaultUserIds}
                        onChange={(ids) =>
                          setAssignmentCriteria((prev) => ({ ...prev, defaultUserIds: ids }))
                        }
                        placeholder={t('orderAssignment.selectDefaultUsers')}
                        searchPlaceholder={t('orderAssignment.searchUsers')}
                        displayKey="label"
                        valueKey="key"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Execute Button ────────────────────────────────────── */}
              <div className={styles.actionButtons}>
                <button
                  onClick={() => navigate('/orders')}
                  className={styles.cancelButton}
                  disabled={isAssigning}
                >
                  <X size={18} />
                  {t('orderAssignment.cancel')}
                </button>
                <button
                  onClick={handleExecuteAssignment}
                  className={styles.executeButton}
                  disabled={isAssigning}
                >
                  {isAssigning ? (
                    <>
                      <div className={styles.loadingSpinner} />
                      {t('orderAssignment.executing')}
                    </>
                  ) : (
                    <>
                      <Play size={18} />
                      {t('orderAssignment.executeAssignment')}
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Reassign / Assign-Unassigned Modal ──────────────────────────── */}
      <ReassignModal
        isOpen={reassignModalOpen}
        onClose={handleReassignModalClose}
        onConfirm={handleReassignConfirm}
        selectedUsers={reassignSourceUsers}
        availableStatuses={availableStatuses}
        users={users}
        products={products}
        isLoading={isReassigning}
        mode={reassignModalMode}
      />
    </div>
  );
};

export default ManualOrderAssignmentPage;